import { ageLabel, isStale } from '../data/snapshot';
import { useSnapshot } from '../data/useSnapshot';

/**
 * One line, on every page, saying how old the numbers are.
 *
 * `apps/web` never needed this: it queries a database on the same machine, so
 * what it renders is what is there. This app renders a file published
 * elsewhere and polled every 30 seconds, which means it can be looking at
 * something hours stale after a PC reboot or an upload failure. Leaving that
 * unsaid would make the page quietly wrong, so it is said here — the age, the
 * generation timestamp, and the reason when a refresh is failing.
 */
export function DataAge() {
  const { status, snapshot, error, now } = useSnapshot();

  if (status === 'unconfigured') {
    return (
      <div className="datanote err">
        <span className="pip" />
        <span>
          No snapshot configured — <b>VITE_SNAPSHOT_URL</b> was not set at build time
        </span>
      </div>
    );
  }

  if (!snapshot) {
    return status === 'error' ? (
      <div className="datanote err">
        <span className="pip" />
        <span>
          Snapshot unreachable — <b>{error ?? 'unknown error'}</b> · retrying every 30s
        </span>
      </div>
    ) : (
      <div className="datanote">
        <span className="pip" />
        <span>Loading snapshot…</span>
      </div>
    );
  }

  const stale = isStale(snapshot.generatedAt, now);
  const cls = error ? 'datanote err' : stale ? 'datanote warn' : 'datanote';

  return (
    <div className={cls}>
      <span className="pip" />
      <span>
        Data <b>{ageLabel(snapshot.generatedAt, now)}</b> · generated {snapshot.generatedAtIso}
      </span>
      {stale ? <span>Agent may have stopped publishing</span> : null}
      {error ? (
        <span>
          Last refresh failed — <b>{error}</b>
        </span>
      ) : null}
    </div>
  );
}
